/** Locale pour le rendu des noms / prénoms à l’écran. */
const NAME_LOCALE = 'fr-FR'

function titleCaseSegment(segment: string): string {
  if (!segment) return ''
  const lower = segment.toLocaleLowerCase(NAME_LOCALE)
  const first = lower.charAt(0).toLocaleUpperCase(NAME_LOCALE)
  return first + lower.slice(1)
}

/**
 * Met en forme un nom ou prénom : première lettre en majuscule, le reste en minuscules,
 * par mot (espaces) et par segment composé (tirets), ex. « JEAN-PIERRE » → « Jean-Pierre ».
 */
export function formatNamePart(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return ''
  return trimmed
    .split(/\s+/)
    .map((word) => word.split('-').map(titleCaseSegment).join('-'))
    .join(' ')
}

/** Affichage « nom prénom » (ordre métier Mon Orga). */
export function formatPersonDisplayName(
  lastName?: string | null,
  firstName?: string | null,
): string {
  const parts = [formatNamePart(lastName), formatNamePart(firstName)].filter((p) => p !== '')
  return parts.join(' ')
}

/**
 * Libellé d’identité pour toasts / dialogues : « Nom Prénom (NNI) ».
 * Si le nom est absent, le NNI seul est renvoyé.
 */
export function formatPersonLabelWithNni(
  lastName?: string | null,
  firstName?: string | null,
  nni?: string | null,
): string {
  const name = formatPersonDisplayName(lastName, firstName)
  const nniLabel = (nni ?? '').trim()
  if (name && nniLabel) return `${name} (${nniLabel})`
  return name || nniLabel
}

function stripDiacriticsForEmail(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '')
}

function normalizeNameForEmailPart(value: string | null | undefined): string {
  const formatted = formatNamePart(value)
  if (!formatted) return ''
  return formatted
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function buildProfessionalEmailLocalPart(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string | null {
  const firstFormatted = normalizeNameForEmailPart(firstName)
  const lastFormatted = normalizeNameForEmailPart(lastName)
  if (!firstFormatted || !lastFormatted) return null
  const localPart = `${stripDiacriticsForEmail(firstFormatted)}.${stripDiacriticsForEmail(lastFormatted)}`.toLowerCase()
  if (!localPart.includes('.') || localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
    return null
  }
  return localPart
}

function normalizeGraphUserDomain(userDomain: string | null | undefined): string | null {
  const trimmedDomain = (userDomain ?? '').trim().replace(/^@+/, '').toLowerCase()
  if (trimmedDomain.length === 0) return null
  if (trimmedDomain.includes('/') || trimmedDomain.includes(' ')) return null
  if (!trimmedDomain.includes('.')) return null
  return trimmedDomain
}

/**
 * UPN Microsoft Graph dérivé du prénom/nom et du domaine Graph de configuration.
 */
export function buildGraphUserPrincipalName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  userDomain: string | null | undefined,
): string | null {
  const localPart = buildProfessionalEmailLocalPart(firstName, lastName)
  if (!localPart) return null
  const normalizedDomain = normalizeGraphUserDomain(userDomain)
  if (!normalizedDomain) return null
  return `${localPart}@${normalizedDomain}`
}
